import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Descriptor-driven filter state for a paged list page, synced to the query
// string so every list view is bookmarkable, shareable and refresh-surviving.
//
// This file is deliberately DOMAIN-FREE. It knows about "a filter" — a param
// name, a default, how to read and write a token — and nothing about leads,
// buyers, statuses or any other vocabulary. The vocabulary lives in a
// per-module descriptor (see pages/leads/leadFilters.ts) so a second list page
// reuses the mechanism without inheriting the first page's nouns.
//
// URL OWNERSHIP IS COOPERATIVE, NOT EXCLUSIVE. Every write starts from the
// CURRENT query string and touches only the keys the descriptor declares, so a
// param the shell knows nothing about — a campaign `?ref=`, a filter a later PR
// adds, anything a teammate pasted — survives a filter change instead of being
// silently dropped. Rebuilding the whole query string from the descriptor would
// be simpler and would quietly destroy those on the first keystroke.
//
// DEFAULTS ARE RESOLVED AT PARSE TIME, never in a post-mount effect. An effect
// that "applies defaults" re-keys every query one render in and double-fetches
// the page (the root-cause class recorded in CLAUDE.md). A value equal to its
// default is never written to the URL either, so a page sitting at its defaults
// has a clean query string and issues exactly the requests it always did.

/** Query-string param carrying the 1-based page number. Omitted at page 1. */
export const LIST_PAGE_PARAM = "page";

/** Fallback quiet period for a filter that asks to be debounced without saying
 *  how long. 300ms matches the search debounce already used across the
 *  prequalification panels. */
export const DEFAULT_FILTER_DEBOUNCE_MS = 300;

/** How the bar renders a filter. `hidden` renders nothing — the filter is set
 *  by a deep link or by page code and only needs URL round-tripping. */
export type ListFilterControl = "search" | "select" | "hidden";

export interface ListFilterOption {
  value: string;
  label: string;
}

export interface ListFilterDef<V = string> {
  /** Value when the param is absent. Never written to the URL. */
  defaultValue: V;
  /**
   * Token -> value. Return `undefined` for anything unrecognised and the
   * default is used, so a hand-edited, stale or hostile URL degrades to the
   * default view instead of forwarding junk to the server. Omit for plain
   * strings.
   */
  parse?(raw: string): V | undefined;
  /**
   * Value -> token, or `null` to omit the param. Omit for plain strings: the
   * built-in rule omits any value equal to the default and stringifies the
   * rest.
   */
  serialize?(value: V): string | null;
  /** Which control {@link ListFilterBar} renders. Defaults to `select`. */
  control?: ListFilterControl;
  /** Accessible name for the rendered control. Omitted when absent. */
  label?: string;
  /** Placeholder for `control: "search"`. */
  placeholder?: string;
  /** Options for `control: "select"`; the first is normally the "All …" row. */
  options?: readonly ListFilterOption[];
  /** Layout-only class for the rendered control (a width, typically). */
  className?: string;
  /** Debounce keystrokes for this many ms before the value reaches the URL. */
  debounceMs?: number;
}

/**
 * A descriptor: param name -> definition. The map key IS the query-string
 * param name and the key of the value in {@link ListFilters.values}, so the two
 * can never drift apart.
 *
 * `parse`/`serialize` are declared as METHODS rather than function properties
 * on purpose: method signatures are bivariant, which is what lets a concrete
 * `ListFilterDef<LeadStatus | "">` satisfy this `unknown`-valued constraint
 * while the hook still infers the precise value type back out.
 */
export type ListFilterDefs = Readonly<Record<string, ListFilterDef<unknown>>>;

export type ListFilterValues<D extends ListFilterDefs> = {
  [K in keyof D]: D[K] extends ListFilterDef<infer V> ? V : never;
};

export interface ListFilters<D extends ListFilterDefs> {
  /** The descriptor, so the bar can render from the same source of truth. */
  defs: D;
  /** URL truth, defaults applied. This is what a query key should be built from. */
  values: ListFilterValues<D>;
  /**
   * What the controls display. Identical to {@link values} except for debounced
   * filters, where it holds text the user has typed that has not yet reached
   * the URL.
   */
  inputs: ListFilterValues<D>;
  /**
   * Set one filter. Debounced filters update {@link inputs} now and the URL
   * after their quiet period; every other filter writes the URL immediately.
   * Either way the page number is reset in the SAME write, so a filter change
   * re-keys the queries exactly once.
   */
  set<K extends keyof D & string>(key: K, value: ListFilterValues<D>[K]): void;
  /** Clear every declared filter (and the page) back to its default. */
  reset(): void;
  /** Token -> value for one filter, for controls that hand back raw strings. */
  parseValue<K extends keyof D & string>(key: K, raw: string): ListFilterValues<D>[K];
  page: number;
  setPage(page: number): void;
  /**
   * Changes whenever any filter value changes; page is deliberately excluded.
   * Use as an effect dependency for things a new result set invalidates, such
   * as a row selection.
   */
  resetToken: string;
}

function tokenFor(def: ListFilterDef<unknown>, value: unknown): string | null {
  if (def.serialize) return def.serialize(value);
  if (value === undefined || value === null) return null;
  if (Object.is(value, def.defaultValue)) return null;
  return String(value);
}

function parseToken(def: ListFilterDef<unknown>, raw: string): unknown {
  if (!def.parse) return raw === "" ? def.defaultValue : raw;
  const parsed = def.parse(raw);
  return parsed === undefined ? def.defaultValue : parsed;
}

function applyValue(
  params: URLSearchParams,
  key: string,
  def: ListFilterDef<unknown>,
  value: unknown,
): void {
  const token = tokenFor(def, value);
  if (token === null) params.delete(key);
  else params.set(key, token);
}

function readPage(params: URLSearchParams, pageParam: string): number {
  const raw = params.get(pageParam);
  if (!raw) return 1;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

/** Options for {@link useListFilters}. Every member is optional and the
 *  defaults reproduce the hook's original behaviour exactly. */
export interface UseListFiltersOptions {
  /**
   * Query-string param that carries the page number. Defaults to
   * {@link LIST_PAGE_PARAM} (`page`).
   *
   * <p>Exists for a list that shares its URL with a SIBLING list: a record page
   * whose own tab strip, drawer or second table already owns `page` can give
   * its inner list a namespaced one (the buyer Research tab uses `rPage`) so
   * paging one never moves the other. A page with one list omits this.</p>
   */
  pageParam?: string;
}

export function useListFilters<D extends ListFilterDefs>(
  defs: D,
  options?: UseListFiltersOptions,
): ListFilters<D> {
  const pageParam = options?.pageParam ?? LIST_PAGE_PARAM;
  const [params, setParams] = useSearchParams();
  const query = params.toString();

  const keys = useMemo(() => Object.keys(defs), [defs]);

  const values = useMemo(() => {
    const source = new URLSearchParams(query);
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      const raw = source.get(key);
      out[key] = raw === null ? defs[key].defaultValue : parseToken(defs[key], raw);
    }
    return out as ListFilterValues<D>;
  }, [defs, keys, query]);

  const page = useMemo(
    () => readPage(new URLSearchParams(query), pageParam),
    [query, pageParam],
  );

  // Read through a ref so `write` can compare against the live query string
  // without taking it as a dependency.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(paramsRef.current);
      mutate(next);
      // A write that changes nothing would still push a history entry and, for
      // a filter set that happens to be identical, re-render for no reason.
      if (next.toString() === paramsRef.current.toString()) return;
      // `replace` so a run of filter edits does not bury the page the user
      // arrived from under a Back-button trail of every keystroke.
      setParams(next, { replace: true });
    },
    [setParams],
  );

  const debouncedKeys = useMemo(
    () => keys.filter((key) => (defs[key].debounceMs ?? 0) > 0),
    [defs, keys],
  );

  // What debounced controls display until their quiet period elapses. Every
  // other control renders `values` directly, so there is exactly one source of
  // truth per control.
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const key of debouncedKeys) {
      initial[key] = tokenFor(defs[key], (values as Record<string, unknown>)[key]) ?? "";
    }
    return initial;
  });

  // The last URL token this hook has seen per debounced key. Resyncing on "the
  // URL object changed" alone would clobber half-typed text whenever some OTHER
  // param moved; this narrows the resync to keys that actually moved.
  const lastSeen = useRef<Record<string, string>>({});

  useEffect(() => {
    setDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of debouncedKeys) {
        const token = tokenFor(defs[key], (values as Record<string, unknown>)[key]) ?? "";
        if (lastSeen.current[key] === token) continue;
        lastSeen.current[key] = token;
        if (next[key] !== token) {
          next[key] = token;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defs, debouncedKeys, values]);

  useEffect(() => {
    const pending = debouncedKeys.filter(
      (key) =>
        (drafts[key] ?? "") !==
        (tokenFor(defs[key], (values as Record<string, unknown>)[key]) ?? ""),
    );
    // Nothing pending — including on mount, where drafts are seeded FROM the
    // URL — means no timer at all, so a clean load never schedules a deferred
    // write that would re-key the queries a beat later.
    if (pending.length === 0) return;
    const delay = Math.min(
      ...pending.map((key) => defs[key].debounceMs ?? DEFAULT_FILTER_DEBOUNCE_MS),
    );
    const timer = window.setTimeout(() => {
      write((next) => {
        for (const key of pending) {
          applyValue(next, key, defs[key], parseToken(defs[key], drafts[key] ?? ""));
        }
        next.delete(pageParam);
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [defs, debouncedKeys, drafts, values, write, pageParam]);

  const set = useCallback(
    <K extends keyof D & string>(key: K, value: ListFilterValues<D>[K]) => {
      const def = defs[key];
      if ((def.debounceMs ?? 0) > 0) {
        setDrafts((prev) => ({ ...prev, [key]: tokenFor(def, value) ?? "" }));
        return;
      }
      write((next) => {
        applyValue(next, key, def, value);
        // A different filter is a different result set; staying on page 4 of
        // the old one shows an empty page and reads as "no matches". Done in
        // the same write so the query key changes once, not twice.
        next.delete(pageParam);
      });
    },
    [defs, write, pageParam],
  );

  const reset = useCallback(() => {
    write((next) => {
      for (const key of keys) next.delete(key);
      next.delete(pageParam);
    });
  }, [keys, write, pageParam]);

  const setPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(1, Math.floor(nextPage) || 1);
      write((next) => {
        if (clamped <= 1) next.delete(pageParam);
        else next.set(pageParam, String(clamped));
      });
    },
    [write, pageParam],
  );

  const parseValue = useCallback(
    <K extends keyof D & string>(key: K, raw: string) =>
      parseToken(defs[key], raw) as ListFilterValues<D>[K],
    [defs],
  );

  const inputs = useMemo(() => {
    if (debouncedKeys.length === 0) return values;
    const merged = { ...values } as Record<string, unknown>;
    for (const key of debouncedKeys) {
      merged[key] = parseToken(defs[key], drafts[key] ?? "");
    }
    return merged as ListFilterValues<D>;
  }, [defs, debouncedKeys, drafts, values]);

  const resetToken = useMemo(
    () =>
      keys
        .map(
          (key) =>
            `${key}=${tokenFor(defs[key], (values as Record<string, unknown>)[key]) ?? ""}`,
        )
        .join("&"),
    [defs, keys, values],
  );

  return { defs, values, inputs, set, reset, parseValue, page, setPage, resetToken };
}
